import Base3DController from './base_3d_controller'

// app/javascript/controllers/canvas_controller.js
export default class extends Base3DController {
  static targets = [
    ...Base3DController.targets,
    'rightFace', 'leftFace', 'topFace', 'bottomFace', 'backFace',
    'rightMirror', 'leftMirror', 'topMirror', 'bottomMirror',
    'depthSlider', 'status', 'selector', 'sideSelector',
  ]

  static values = {
    ...Base3DController.values,
    boxDepth: { type: Number, default: 18 }, // Override default depth
  }

  initialize() {
    super.initialize()

    // Side-specific color options
    this.colors = {
      black: ['#3c3936', '#46423f', '#5f5c5a, #161615', '#585858, #1f1e1e'],
      white: ['#e8e6e3', '#f0eeeb', '#e4e2df, #b5b3b0', '#ececec, #bab9b6'],
      transparent: ['transparent', 'transparent'],
      mirrored: ['transparent', 'transparent'],
    }
  }

  childConnect() {
    // Initialize current side style
    this.currentSideStyle = 'mirrored'
    this.currentImageUrl = null

    // Canvas-specific event listeners
    if (this.hasSceneTarget) {
      this.sceneTarget.addEventListener('wheel', this.handleWheel.bind(this))
    }

    if (this.hasDepthSliderTarget) {
      this.depthSliderTarget.addEventListener('input', this.handleDepthChange.bind(this))
    }

    // Side selector event listeners
    if (this.hasSelectorTarget) {
      this.selectorTarget.addEventListener('click', () => {
        this.sideSelectorTarget.click()
      })
    }

    if (this.hasSideSelectorTarget) {
      this.sideSelectorTarget.addEventListener('change', this.updateSides.bind(this))
    }
  }

  childDisconnect() {
    if (this.hasSceneTarget) {
      this.sceneTarget.removeEventListener('wheel', this.handleWheel.bind(this))
    }

    if (this.hasDepthSliderTarget) {
      this.depthSliderTarget.removeEventListener('input', this.handleDepthChange.bind(this))
    }

    if (this.hasSelectorTarget) {
      this.selectorTarget.removeEventListener('click', () => {
        this.sideSelectorTarget.click()
      })
    }

    if (this.hasSideSelectorTarget) {
      this.sideSelectorTarget.removeEventListener('change', this.updateSides.bind(this))
    }
  }

  // Override dimension updates for canvas box
  updateChildDimensions() {
    // Set box size
    if (this.hasBoxTarget) {
      this.boxTarget.style.width = `${this.boxWidthValue}px`
      this.boxTarget.style.height = `${this.boxHeightValue}px`
    }

    // Calculate half dimensions for positioning
    const halfWidth = this.boxWidthValue / 2
    const halfHeight = this.boxHeightValue / 2
    const halfDepth = this.boxDepthValue / 2

    // Update front and back faces
    if (this.hasFrontFaceTarget) {
      this.frontFaceTarget.style.width = `${this.boxWidthValue}px`
      this.frontFaceTarget.style.height = `${this.boxHeightValue}px`
      this.frontFaceTarget.style.transform = `translateZ(${halfDepth}px)`
    }

    if (this.hasBackFaceTarget) {
      this.backFaceTarget.style.width = `${this.boxWidthValue}px`
      this.backFaceTarget.style.height = `${this.boxHeightValue}px`
      this.backFaceTarget.style.transform = `rotateY(180deg) translateZ(${halfDepth}px)`
    }

    // Update right and left faces - position from the center
    if (this.hasRightFaceTarget) {
      this.rightFaceTarget.style.width = `${this.boxDepthValue}px`
      this.rightFaceTarget.style.height = `${this.boxHeightValue}px`
      this.rightFaceTarget.style.transform = `rotateY(90deg) translateZ(${halfWidth}px)`
      this.rightFaceTarget.style.left = `${(this.boxWidthValue - this.boxDepthValue) / 2}px`
      this.rightFaceTarget.style.top = '0'
    }

    if (this.hasLeftFaceTarget) {
      this.leftFaceTarget.style.width = `${this.boxDepthValue}px`
      this.leftFaceTarget.style.height = `${this.boxHeightValue}px`
      this.leftFaceTarget.style.transform = `rotateY(-90deg) translateZ(${halfWidth}px)`
      this.leftFaceTarget.style.left = `${(this.boxWidthValue - this.boxDepthValue) / 2}px`
      this.leftFaceTarget.style.top = '0'
    }

    // Update top and bottom faces - position from the center
    if (this.hasTopFaceTarget) {
      this.topFaceTarget.style.width = `${this.boxWidthValue}px`
      this.topFaceTarget.style.height = `${this.boxDepthValue}px`
      this.topFaceTarget.style.transform = `rotateX(90deg) translateZ(${halfHeight}px)`
      this.topFaceTarget.style.left = '0'
      this.topFaceTarget.style.top = `${(this.boxHeightValue - this.boxDepthValue) / 2}px`
    }

    if (this.hasBottomFaceTarget) {
      this.bottomFaceTarget.style.width = `${this.boxWidthValue}px`
      this.bottomFaceTarget.style.height = `${this.boxDepthValue}px`
      this.bottomFaceTarget.style.transform = `rotateX(-90deg) translateZ(${halfHeight}px)`
      this.bottomFaceTarget.style.left = '0'
      this.bottomFaceTarget.style.top = `${(this.boxHeightValue - this.boxDepthValue) / 2}px`
    }

    // Ensure canvas stays within container bounds
    this.scaleToFit()
  }

  // Scale the canvas to fit within the scene container
  scaleToFit() {
    if (!this.hasSceneTarget || !this.hasBoxContainerTarget) return

    const sceneRect = this.sceneTarget.getBoundingClientRect()
    const sceneWidth = sceneRect.width
    const sceneHeight = sceneRect.height

    // Account for padding/margin (10% safety margin)
    const maxWidth = sceneWidth * 0.9
    const maxHeight = sceneHeight * 0.9

    // Get current canvas dimensions including depth for 3D calculations
    const canvasWidth = this.boxWidthValue
    const canvasHeight = this.boxHeightValue
    const canvasDepth = this.boxDepthValue

    // Calculate the maximum possible size when rotated (worst case)
    const maxRotatedWidth = Math.sqrt(canvasWidth * canvasWidth + canvasDepth * canvasDepth)
    const maxRotatedHeight = Math.sqrt(canvasHeight * canvasHeight + canvasDepth * canvasDepth)

    // Calculate scale factor to fit within scene
    const scaleX = maxWidth / maxRotatedWidth
    const scaleY = maxHeight / maxRotatedHeight
    const scale = Math.min(scaleX, scaleY, 1) // Never scale up beyond 100%

    // Apply scale to the box container
    this.boxContainerTarget.style.transform
      = `translate(-50%, -50%) scale(${scale}) rotateX(${this.rotateXValue}deg) rotateY(${this.rotateYValue}deg)`
  }

  // Depth slider handler
  handleDepthChange(e) {
    this.updateBoxDepth(parseInt(e.target.value))
  }

  boxDepthValueChanged() {
    this.updateBoxDimensions(this.initialBoxSize.width, this.initialBoxSize.height, true)
  }

  updateBoxDepth(depth) {
    this.boxDepthValue = depth
  }

  handleWheel(e) {
    // Add wheel zoom functionality if needed
    e.preventDefault()
    // Implementation for wheel zoom
  }

  // Override image handling for canvas
  handleChildImageLoad(imageUrl, file) {
    // Store the current image URL
    this.currentImageUrl = imageUrl

    // Apply the current side style with the new image
    this.applySideStyle(this.currentSideStyle)

    // Update status if target exists
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = `Image loaded: ${file.name}`
    }
  }

  // Override error handling
  handleImageError(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
    }
  }

  // Handle side selector changes
  updateSides(event) {
    const selectedStyle = event ? event.target.value : this.sideSelectorTarget.value
    this.currentSideStyle = selectedStyle
    this.applySideStyle(selectedStyle)
  }

  // Apply the selected side style
  applySideStyle(style) {
    if (style === 'mirrored') {
      this.setMirroredBackgrounds(this.currentImageUrl)
    }
    else {
      this.clearMirroredBackgrounds()
    }

    this.setSolidBackgrounds(this.colors[style])
  }

  setMirroredBackgrounds(imageUrl) {
    if (!imageUrl) return

    if (this.hasRightMirrorTarget) {
      this.rightMirrorTarget.style.backgroundImage = `url('${imageUrl}')`
      this.rightMirrorTarget.style.display = 'block'
    }
    if (this.hasLeftMirrorTarget) {
      this.leftMirrorTarget.style.backgroundImage = `url('${imageUrl}')`
      this.leftMirrorTarget.style.display = 'block'
    }
    if (this.hasTopMirrorTarget) {
      this.topMirrorTarget.style.backgroundImage = `url('${imageUrl}')`
      this.topMirrorTarget.style.display = 'block'
    }
    if (this.hasBottomMirrorTarget) {
      this.bottomMirrorTarget.style.backgroundImage = `url('${imageUrl}')`
      this.bottomMirrorTarget.style.display = 'block'
    }
  }

  clearMirroredBackgrounds() {
    if (this.hasRightMirrorTarget) {
      this.rightMirrorTarget.style.backgroundImage = 'none'
      this.rightMirrorTarget.style.display = 'none'
    }
    if (this.hasLeftMirrorTarget) {
      this.leftMirrorTarget.style.backgroundImage = 'none'
      this.leftMirrorTarget.style.display = 'none'
    }
    if (this.hasTopMirrorTarget) {
      this.topMirrorTarget.style.backgroundImage = 'none'
      this.topMirrorTarget.style.display = 'none'
    }
    if (this.hasBottomMirrorTarget) {
      this.bottomMirrorTarget.style.backgroundImage = 'none'
      this.bottomMirrorTarget.style.display = 'none'
    }
  }

  setSolidBackgrounds(colors) {
    if (this.hasRightFaceTarget) {
      if (colors[2]) {
        this.rightFaceTarget.style.background = `linear-gradient(to right, ${colors[2]})`
      }
      else {
        this.rightFaceTarget.style.backgroundColor = colors[0]
      }
    }
    if (this.hasLeftFaceTarget) {
      if (colors[2]) {
        this.leftFaceTarget.style.background = `linear-gradient(to left, ${colors[2]})`
      }
      else {
        this.leftFaceTarget.style.backgroundColor = colors[1]
      }
    }
    if (this.hasTopFaceTarget) {
      if (colors[3]) {
        this.topFaceTarget.style.background = `linear-gradient(to top, ${colors[3]})`
      }
      else {
        this.topFaceTarget.style.backgroundColor = colors[0]
      }
    }
    if (this.hasBottomFaceTarget) {
      if (colors[3]) {
        this.bottomFaceTarget.style.background = `linear-gradient(to bottom, ${colors[3]})`
      }
      else {
        this.bottomFaceTarget.style.backgroundColor = colors[1]
      }
    }
  }
}
